<template>
  <div>
    <IconField class="relative">
      <i
        v-if="searchQuery"
        class="pi pi-filter-fill absolute top-2/4 -mt-2 left-3 text-surface-400 dark:text-surface-600"
      />
      <i
        v-else
        class="pi pi-filter absolute top-2/4 -mt-2 left-3 text-surface-400 dark:text-surface-600"
      />
      <InputText
        ref="filterInput"
        v-model="searchQuery"
        type="search"
        :placeholder="t('placeholder.quickfilter')"
        class="pl-10"
        data-testid="datatable-quickfilter-input"
        @input="onSearchInput"
      />
      <InputIcon
        v-if="searchQuery"
        class="mdi mdi-close absolute top-1/2 right-3 cursor-pointer"
        title=""
        data-testid="datatable-quickfilter-clear"
        @click="clearFilter"
      />
    </IconField>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { hasLogging } from '@/plugins/global-config';

import type { ColumnConfig } from './/dataTableTypes';

const props = defineProps<{
  data: any[];
  columns: ColumnConfig[];
  focusedColumns?: string[];
  minLength?: number;
  enableAutoRestore?: boolean;
  filterString?: string;
}>();

const emit = defineEmits<{
  (event: 'update:filteredData', value: any[]): void;
  (event: 'update:filterString', value: string): void;
}>();

const { t } = useI18n();

const searchQuery = ref(props.filterString || ''); // Set the initial value to the passed filter string

const lastSuccessfulQuery = ref(''); // Last successful filter string
let restoreTimeout: ReturnType<typeof setTimeout> | null = null;
const reactiveFocusedColumns = computed(() => props.focusedColumns || []);

// Computed property for filtered data
const filteredData = computed(() => {
  if (searchQuery.value.length === 0) {
    return props.data;
  }

  const queryParts = searchQuery.value.toLowerCase().split(' ');

  // 1) First filter by the selected columns
  let result = filterData(queryParts, props.focusedColumns || []);

  // 2) If no results were found, search all columns
  if (result.length === 0) {
    result = filterData(
      queryParts,
      props.columns.map((col) => col.field),
    );
  }

  // 3) If no results are found and the auto-restore feature is enabled, reset the last successful filter string after 3 seconds
  // ==> moved to watch

  hasLogging('sey-datatable') && console.log('[data-table] FINAL FILTERED RESULT:', result);
  return result;
});

function filteredResults({
  columns,
  queryParts,
  item,
}: {
  columns: string[];
  queryParts: string[];
  item: any;
}) {
  // build fulltext string of all searchable columns
  const globalSearchString = columns
    .map((col) => item[col]?.meta?.().searchString ?? '')
    .join(' ')
    .toLowerCase();

  // Matches if all parts are contained
  return queryParts.every((part) => globalSearchString.includes(part));
}

function filterData(queryParts: string[], columns: string[]) {
  hasLogging('sey-datatable') && console.log('[data-table] COLUMNS TO FILTER:', columns);

  const strictResult = props.data.filter((item) => {
    const result = filteredResults({ columns, queryParts, item });
    if (result) {
      hasLogging('sey-datatable') &&
        console.log('[data-table]  STRICT FILTER: Matching item:', item);
    }
    return result;
  });

  if (strictResult.length) {
    hasLogging('sey-datatable') && console.log('[data-table] STRICT FILTER RESULT:', strictResult);
    return strictResult;
  } else {
    const fallbackResult = props.data.filter((item) => {
      const result = filteredResults({ columns, queryParts, item });
      if (result) {
        hasLogging('sey-datatable') &&
          console.log('[data-table] FALLBACK FILTER: Matching item:', item);
      }
      return result;
    });
    hasLogging('sey-datatable') &&
      console.log('[data-table] FALLBACK FILTER RESULT:', fallbackResult);
    return fallbackResult;
  }
}

function onSearchInput() {
  // Custom logic when search input changes, if needed
}

function clearFilter() {
  searchQuery.value = '';
  emit('update:filteredData', props.data);
  emit('update:filterString', '');
}

function emitValues() {
  emit('update:filteredData', filteredData.value);
  emit('update:filterString', searchQuery.value);
}

watch([searchQuery, filteredData, reactiveFocusedColumns], () => {
  emitValues();
});

watch(
  () => props.filterString,
  (newFilterString) => {
    if (newFilterString && !searchQuery.value) {
      searchQuery.value = newFilterString;
    }
  },
  { immediate: true },
);

// Auto-restore: reatcs on changes of filtered result
watch(filteredData, (result) => {
  if (!props.enableAutoRestore) return;
  if (result.length === 0) {
    if (restoreTimeout) clearTimeout(restoreTimeout);
    restoreTimeout = setTimeout(() => {
      if (lastSuccessfulQuery.value) {
        searchQuery.value = lastSuccessfulQuery.value;
      }
    }, 3000);
  } else {
    lastSuccessfulQuery.value = searchQuery.value;
    if (restoreTimeout) {
      clearTimeout(restoreTimeout);
      restoreTimeout = null;
    }
  }
});

onBeforeUnmount(() => {
  if (restoreTimeout) clearTimeout(restoreTimeout);
});

onMounted(() => {
  emitValues();
});
</script>
